import React, {useCallback, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {useNavigate} from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import {prysmDB} from '../utils/database';
import apiService from '../services/apiService';
import clientLogger from '../utils/clientLogger';

// Maximum file size: 8MB (reasonable for high-quality license photos)
const MAX_FILE_SIZE = 8 * 1024 * 1024;

// Error Modal Component
const ErrorModal = ({errors, errorDetails, onClose, isVisible}) => {
    if (!isVisible || !errors) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
                className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-lg w-full transform transition-all duration-300 scale-100">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-red-400">Upload Issues</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-200 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
                <div className="space-y-3">
                    <div className="flex items-start bg-gray-700/50 p-3 rounded-lg">
                        <span className="text-red-400 mr-2">•</span>
                        <span className="text-gray-200">{errors}</span>
                    </div>
                    {errorDetails && (
                        <div className="bg-red-900/20 p-4 rounded-lg border border-red-700">
                            <div className="flex items-center mb-2">
                                <svg className="w-4 h-4 mr-2 text-red-400" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                                </svg>
                                <span className="text-sm font-medium text-red-200">{errorDetails.type}</span>
                            </div>
                            <p className="text-sm text-red-300 mb-3">{errorDetails.message}</p>
                            {errorDetails.suggestions && errorDetails.suggestions.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-red-200 mb-2">Suggestions:</p>
                                    <ul className="text-sm text-red-300 space-y-1">
                                        {errorDetails.suggestions.map((suggestion, index) => (
                                            <li key={index} className="flex items-start">
                                                <span className="text-red-400 mr-2">•</span>
                                                <span>{suggestion}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const LicenseUpload = () => {
    const [frontFile, setFrontFile] = useState(null);
    const [backFile, setBackFile] = useState(null);
    const [frontPreview, setFrontPreview] = useState(null);
    const [backPreview, setBackPreview] = useState(null);
    const [error, setError] = useState('');
    const [consolidatedErrors, setConsolidatedErrors] = useState(null);
    const [errorDetails, setErrorDetails] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const navigate = useNavigate();

    // Comprehensive error handling function
    const handleError = (error, context = 'Unknown') => {
        console.error(`Error in ${context}:`, error);

        // Log error to clientPrysm logger
        clientLogger.logError('license_upload_error', `License upload error in ${context}: ${error.message}`, error, {
            action: context.toLowerCase().replace(/\s+/g, '_'),
            component: 'LicenseUpload',
            context: context,
            errorType: error.name || 'Unknown',
            errorMessage: error.message || 'No message available'
        });

        // Clear previous error details
        setErrorDetails(null);

        // Handle different types of errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            setError('Network Error: Unable to connect to the server. Please check your internet connection and try again.');
            setErrorDetails({
                type: 'NetworkError',
                message: 'Failed to connect to the server',
                context: context,
                suggestions: [
                    'Check your internet connection',
                    'Try refreshing the page',
                    'Wait a few minutes and try again',
                    'Contact support if the problem continues'
                ]
            });
        } else if (error.message && error.message.includes('timeout')) {
            setError('Timeout Error: The request took too long to complete. Please try again.');
            setErrorDetails({
                type: 'TimeoutError',
                message: 'Request timed out',
                context: context,
                suggestions: [
                    'Try again with smaller images',
                    'Check your internet connection',
                    'Wait a few minutes and try again'
                ]
            });
        } else if (error.message && error.message.includes('413')) {
            setError('File Too Large: The image files are too large. Please use smaller images.');
            setErrorDetails({
                type: 'FileSizeError',
                message: 'Image files exceed maximum size limit',
                context: context,
                suggestions: [
                    'Compress the images before uploading',
                    'Use images with lower resolution',
                    'Try different image formats'
                ]
            });
        } else if (error.message && error.message.includes('400')) {
            setError('Invalid Images: The image formats or content are not supported. Please try different images.');
            setErrorDetails({
                type: 'InvalidImageError',
                message: 'Image formats or content not supported',
                context: context,
                suggestions: [
                    'Use JPG, PNG, GIF, or WEBP format',
                    'Ensure the images are not corrupted',
                    'Try different images'
                ]
            });
        } else {
            setError(`Upload Error: ${error.message || 'An unexpected error occurred. Please try again.'}`);
            setErrorDetails({
                type: 'UploadError',
                message: error.message || 'An unexpected error occurred',
                context: context,
                suggestions: [
                    'Try uploading different images',
                    'Check your internet connection',
                    'Refresh the page and try again',
                    'Contact support if the problem continues'
                ]
            });
        }
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (frontFile || backFile) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [frontFile, backFile]);

    useEffect(() => {
        // Add effect to show error modal when errors occur
        if (error) {
            setShowErrorModal(true);
        }
    }, [error]);


    // Helper function to format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };


    // Function to validate file
    const validateFile = (file) => {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            throw new Error('Please upload a valid image file (JPG, PNG, GIF, or WEBP)');
        }

        if (file.size > MAX_FILE_SIZE) {
            throw new Error(`File size should be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
        }
    };

    // Dropzone for front of license
    const onDropFront = useCallback((acceptedFiles) => {
        setError('');
        setErrorDetails(null);
        setConsolidatedErrors(null);
        const selectedFile = acceptedFiles[0];

        try {
            validateFile(selectedFile);
            setFrontFile(selectedFile);
            setFrontPreview(URL.createObjectURL(selectedFile));

            // Log successful file validation
            clientLogger.logError('license_front_file_validated', 'Front license file validation successful', null, {
                action: 'front_file_validation',
                component: 'LicenseUpload',
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                fileType: selectedFile.type
            });
        } catch (error) {
            handleError(error, 'Front License File Validation');
        }
    }, []);

    // Dropzone for back of license
    const onDropBack = useCallback((acceptedFiles) => {
        setError('');
        setErrorDetails(null);
        setConsolidatedErrors(null);
        const selectedFile = acceptedFiles[0];

        try {
            validateFile(selectedFile);
            setBackFile(selectedFile);
            setBackPreview(URL.createObjectURL(selectedFile));

            // Log successful file validation
            clientLogger.logError('license_back_file_validated', 'Back license file validation successful', null, {
                action: 'back_file_validation',
                component: 'LicenseUpload',
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                fileType: selectedFile.type
            });
        } catch (error) {
            handleError(error, 'Back License File Validation');
        }
    }, []);

    const {
        getRootProps: getRootPropsFront,
        getInputProps: getInputPropsFront,
        isDragActive: isDragActiveFront
    } = useDropzone({
        onDrop: onDropFront,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
        },
        maxFiles: 1
    });

    const {
        getRootProps: getRootPropsBack,
        getInputProps: getInputPropsBack,
        isDragActive: isDragActiveBack
    } = useDropzone({
        onDrop: onDropBack,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
        },
        maxFiles: 1
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!frontFile || !backFile) {
            setError('Please upload both front and back of your driver\'s license');
            setErrorDetails({
                type: 'ValidationError',
                message: 'Missing required files',
                context: 'handleSubmit',
                suggestions: [
                    'Upload both front and back images of your driver\'s license',
                    'Use the drag and drop areas or click to browse',
                    'Ensure both files are valid image formats'
                ]
            });
            return;
        }

        setIsProcessing(true);
        setError('');
        setErrorDetails(null);
        setConsolidatedErrors(null);

        try {
            // Upload license images to server
            console.log('Uploading license images to server...');
            const response = await apiService.uploadLicense(frontFile, backFile);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Upload failed: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            console.log('License upload successful:', result);

            // Log successful upload
            clientLogger.logError('license_upload_success', 'License upload completed successfully', null, {
                action: 'license_upload',
                component: 'LicenseUpload',
                frontImageId: result.data?.frontImageId,
                backImageId: result.data?.backImageId,
                frontFileSize: frontFile.size,
                backFileSize: backFile.size,
                frontFileType: frontFile.type,
                backFileType: backFile.type,
                frontFileName: frontFile.name,
                backFileName: backFile.name
            });

            // Store the server response in storage for reference
            try {
                // Store in license_data store
                const licenseData = {
                    uploadResponse: result,
                    uploadTimestamp: new Date().toISOString(),
                    frontImageId: result.data?.frontImageId,
                    backImageId: result.data?.backImageId,
                    frontFileName: frontFile.name,
                    backFileName: backFile.name,
                    frontFileSize: frontFile.size,
                    backFileSize: backFile.size,
                    frontFileType: frontFile.type,
                    backFileType: backFile.type,
                    uploaded: true
                };

                await prysmDB.set('license_data', 'current_license', licenseData);
                console.log('[LicenseUpload] License data saved to storage:', licenseData);

                // Also update registration_data for backward compatibility
                const registrationData = await prysmDB.get('registration_data', 'current_registration');
                if (registrationData) {
                    registrationData.licenseUploadResponse = result;
                    registrationData.licenseUploadTimestamp = new Date().toISOString();
                    registrationData.frontLicenseImageId = result.data?.frontImageId;
                    registrationData.backLicenseImageId = result.data?.backImageId;
                    await prysmDB.set('registration_data', 'current_registration', registrationData);
                    console.log('[LicenseUpload] License upload response also saved to registration_data');
                }
            } catch (dbError) {
                console.warn('[LicenseUpload] Could not save license upload response to storage:', dbError);
            }

            setError('Driver\'s license uploaded successfully!');
            setTimeout(() => {
                navigate('/checkout');
            }, 1500);

        } catch (err) {
            handleError(err, 'License Upload');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-4 sm:py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700">
                    <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-4 sm:p-8">
                        <h1 className="text-xl sm:text-3xl font-bold text-white text-center">
                            Upload Driver's License
                        </h1>
                        <p className="text-center text-pink-100 mt-2">
                            Please upload clear photos of both sides of your driver's license
                        </p>
                    </div>

                    <div className="p-4 sm:p-8">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && error.includes('successfully') && (
                                <div className="rounded-lg p-3 sm:p-4 border bg-green-900/50 border-green-700">
                                    <p className="text-sm text-green-300">{error}</p>
                                </div>
                            )}

                            {error && !error.includes('successfully') && (
                                <div className="rounded-lg p-3 sm:p-4 border bg-red-900/50 border-red-700">
                                    <p className="text-sm text-red-300">{error}</p>
                                    {errorDetails && (
                                        <div className="mt-3 p-3 bg-red-800/30 rounded-lg border border-red-600">
                                            <div className="flex items-center mb-2">
                                                <svg className="w-4 h-4 mr-2 text-red-400" fill="none"
                                                     stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                                                </svg>
                                                <span
                                                    className="text-sm font-medium text-red-200">{errorDetails.type}</span>
                                            </div>
                                            <p className="text-xs text-red-300 mb-2">{errorDetails.message}</p>
                                            {errorDetails.suggestions && errorDetails.suggestions.length > 0 && (
                                                <div>
                                                    <p className="text-xs font-medium text-red-200 mb-1">Suggestions:</p>
                                                    <ul className="text-xs text-red-300 space-y-1">
                                                        {errorDetails.suggestions.map((suggestion, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="text-red-400 mr-1">•</span>
                                                                <span>{suggestion}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Front of License */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-200">Front of License</h3>
                                    <div
                                        {...getRootPropsFront()}
                                        className={`border-2 border-dashed rounded-xl p-4 sm:p-6 text-center cursor-pointer transition-colors duration-200 min-h-[200px] flex items-center justify-center ${isDragActiveFront
                                            ? 'border-pink-500 bg-gray-700'
                                            : 'border-gray-600 hover:border-pink-400'
                                        }`}
                                    >
                                        <input {...getInputPropsFront()} />
                                        {frontPreview ? (
                                            <div className="space-y-3 sm:space-y-4">
                                                <img
                                                    src={frontPreview}
                                                    alt="Front of License Preview"
                                                    className="mx-auto h-32 sm:h-48 w-auto object-contain rounded-lg"
                                                />
                                                <p className="text-xs sm:text-sm text-gray-400">
                                                    Tap or drag to change the image
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3 sm:space-y-4">
                                                <div className="mx-auto h-10 sm:h-12 w-10 sm:w-12 text-pink-500">
                                                    <svg
                                                        className="h-full w-full"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-300">
                                                        Tap to select front image
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        or drag and drop on desktop
                                                    </p>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    JPG, PNG, GIF, WEBP (max {MAX_FILE_SIZE / (1024 * 1024)}MB)
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Back of License */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-200">Back of License</h3>
                                    <div
                                        {...getRootPropsBack()}
                                        className={`border-2 border-dashed rounded-xl p-4 sm:p-6 text-center cursor-pointer transition-colors duration-200 min-h-[200px] flex items-center justify-center ${isDragActiveBack
                                            ? 'border-pink-500 bg-gray-700'
                                            : 'border-gray-600 hover:border-pink-400'
                                        }`}
                                    >
                                        <input {...getInputPropsBack()} />
                                        {backPreview ? (
                                            <div className="space-y-3 sm:space-y-4">
                                                <img
                                                    src={backPreview}
                                                    alt="Back of License Preview"
                                                    className="mx-auto h-32 sm:h-48 w-auto object-contain rounded-lg"
                                                />
                                                <p className="text-xs sm:text-sm text-gray-400">
                                                    Tap or drag to change the image
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3 sm:space-y-4">
                                                <div className="mx-auto h-10 sm:h-12 w-10 sm:w-12 text-pink-500">
                                                    <svg
                                                        className="h-full w-full"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-300">
                                                        Tap to select back image
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        or drag and drop on desktop
                                                    </p>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    JPG, PNG, GIF, WEBP (max {MAX_FILE_SIZE / (1024 * 1024)}MB)
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="text-center p-4 bg-gray-700/50 rounded-xl border border-gray-600">
                                    <h3 className="text-lg font-semibold text-white mb-2">License Requirements</h3>
                                    <ul className="text-gray-300 space-y-2 text-sm">
                                        <li className="flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-pink-500" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                            Take clear, well-lit photos of both sides
                                        </li>
                                        <li className="flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-pink-500" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                            Ensure all text is readable and license is fully visible
                                        </li>
                                        <li className="flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-pink-500" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                            Avoid glare and shadows on the license
                                        </li>
                                        <li className="flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-pink-500" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                            License must be valid and not expired
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={!frontFile || !backFile || isProcessing}
                                className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-all duration-200 disabled:opacity-50 active:scale-95"
                            >
                                {isProcessing ? (
                                    <LoadingSpinner text="Processing license images..."/>
                                ) : (
                                    'Upload License Images'
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Add Error Modal */}
            <ErrorModal
                errors={error}
                errorDetails={errorDetails}
                onClose={() => {
                    setShowErrorModal(false);
                    if (error.includes('successfully')) {
                        setError('');
                    }
                }}
                isVisible={showErrorModal}
            />
        </div>
    );
};

export default LicenseUpload;
